import React, { useEffect, useMemo, useState } from "react";
import { Megaphone, Star } from "@phosphor-icons/react";
import { api, fileUrl } from "../lib/api";

const FALLBACK_SETTINGS = {
  scroll_duration_ms: 30000,
  promo_duration_ms: 4000,
  height_px: 44,
  background_opacity: 85,
  scroll_text_color: "#e4e4e7",
  cta_text_color: "#ff8a3c",
  promo_text_color: "#ff8a3c",
  font_family: "Outfit",
  letter_spacing_em: 0.2,
  font_weight: 700,
  enabled: true,
};

function buildTickerItems(artists, ctaColor) {
  const items = [];
  artists.forEach((name, i) => {
    items.push({ kind: "artist", text: name });
    if ((i + 1) % 3 === 0) items.push({ kind: "cta", text: "Vote for your favorite song" });
  });
  if (items[items.length - 1]?.kind !== "cta") {
    items.push({ kind: "cta", text: "Vote for your favorite song" });
  }
  return items;
}

export default function MarqueeTicker({ artists }) {
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [banners, setBanners] = useState([]);
  const [phase, setPhase] = useState("scroll");
  const [bannerIdx, setBannerIdx] = useState(0);

  // Load settings + active banners. Re-poll every 60s so admin changes show up.
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/ticker");
        if (mounted) {
          setSettings({ ...FALLBACK_SETTINGS, ...(data.settings || {}) });
          setBanners(data.banners || []);
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Ticker config refresh failed:", err?.message);
        }
        /* keep fallback */
      }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  useEffect(() => {
    if (!settings.enabled) return;
    const dur = phase === "scroll" ? settings.scroll_duration_ms : settings.promo_duration_ms;
    const t = setTimeout(() => {
      setPhase((p) => {
        const nextPhase = p === "scroll" ? "promo" : "scroll";
        if (nextPhase === "promo") {
          setBannerIdx((i) => (banners.length > 0 ? (i + 1) % banners.length : 0));
        }
        return nextPhase;
      });
    }, dur);
    return () => clearTimeout(t);
  }, [phase, settings.scroll_duration_ms, settings.promo_duration_ms, settings.enabled, banners.length]);

  const items = useMemo(() => buildTickerItems(artists || [], settings.cta_text_color), [artists, settings.cta_text_color]);
  const looped = useMemo(() => [...items, ...items], [items]);

  if (!settings.enabled || !artists || artists.length === 0) return null;

  const bgStyle = {
    backgroundColor: `rgba(12, 9, 7, ${(settings.background_opacity ?? 85) / 100})`,
    minHeight: `${settings.height_px}px`,
  };
  const baseTextStyle = {
    fontFamily: settings.font_family,
    fontWeight: settings.font_weight,
    letterSpacing: `${settings.letter_spacing_em}em`,
  };

  const activeBanner = banners[bannerIdx] || null;
  const promoColor = activeBanner?.text_color || settings.promo_text_color;

  return (
    <div
      className="relative border-y border-neon-cyan/20 overflow-hidden select-none"
      style={bgStyle}
      data-testid="marquee-ticker"
      aria-live="polite"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-ink-900 to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-ink-900 to-transparent z-10" />

      {phase === "scroll" ? (
        <div
          className="flex items-center whitespace-nowrap will-change-transform animate-marquee"
          style={{ animationDuration: `${settings.scroll_duration_ms}ms`, minHeight: `${settings.height_px}px` }}
          data-testid="marquee-scroll"
        >
          {looped.map((it, i) => (
            <span
              key={`${it.kind}-${i}`}
              className="flex items-center gap-3 px-7 text-xs sm:text-sm uppercase"
              style={{
                ...baseTextStyle,
                color: it.kind === "cta" ? settings.cta_text_color : settings.scroll_text_color,
              }}
            >
              {it.kind === "cta" ? (
                <Star size={14} weight="fill" style={{ color: settings.cta_text_color }} />
              ) : (
                <span className="text-neon-cyan/60 font-mono text-[10px]">●</span>
              )}
              {it.text}
            </span>
          ))}
        </div>
      ) : (
        <div
          className="flex items-center justify-center gap-3 px-6 text-xs sm:text-sm uppercase animate-pulse-glow"
          style={{ ...baseTextStyle, color: promoColor, minHeight: `${settings.height_px}px` }}
          data-testid="marquee-promo"
        >
          {activeBanner?.image_path && (
            <img
              src={fileUrl(activeBanner.image_path)}
              alt=""
              className="h-7 w-auto rounded-sm object-cover"
            />
          )}
          {activeBanner ? (
            activeBanner.click_url ? (
              <a
                href={activeBanner.click_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline underline-offset-4"
                style={{ color: promoColor }}
                data-testid="marquee-promo-link"
              >
                {activeBanner.text}
              </a>
            ) : (
              <span data-testid="marquee-promo-text">{activeBanner.text}</span>
            )
          ) : (
            <>
              <Megaphone size={16} weight="fill" />
              <span>Ask about promos</span>
              <span className="text-zinc-600">·</span>
              <span className="text-white">Your promo here</span>
              <span className="text-zinc-600">·</span>
              <a
                href="#contact"
                className="hover:underline underline-offset-4"
                style={{ color: promoColor }}
                data-testid="marquee-promo-link"
              >
                Contact the Shoow
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
